import { useState, useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import { TYPOGRAPHY, RADIUS } from "../../theme/tokens";
import { supabaseClient, dbLoadSCCO } from "../../db";
import { dbCheckLockout, dbRecordFailedLogin, dbResetLoginAttempts } from "../../db/lockout";
import { dbRecordLogin } from "../../db/loginLog";
import { saveSession } from "../../utils/sessionUtils";
import { biometricAvailable, bioLogin } from "../../hooks/useAuth";
import { loadBioCreds, saveBioCreds, clearBioCreds } from "../../utils/bioStorage";
import { SA_AIRLINES, LHG_CARRIERS, Button } from "../shared";
function CustomSelect({ value, onChange, options, inputStyle, placeholder }) {
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = e => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const handleKeyDown = e => {
    if (e.key === "Escape") { setOpen(false); return; }
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault(); setOpen(true); return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHovered(h => Math.min((h ?? -1) + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHovered(h => Math.max((h ?? options.length) - 1, 0)); }
    else if (e.key === "Enter" && hovered !== null) { onChange(options[hovered].value); setOpen(false); }
  };

  const selected = options.find(o => o.value === value);
  const isPlaceholder = placeholder && (!value || value === "");

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <div
        role="combobox" aria-expanded={open} tabIndex={0}
        onClick={() => setOpen(o => !o)}
        onKeyDown={handleKeyDown}
        style={{
          ...inputStyle,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          cursor: "pointer", userSelect: "none", outline: "none",
          color: isPlaceholder ? "rgba(255,255,255,0.4)" : inputStyle.color,
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {selected ? selected.label : placeholder}
        </span>
        <span style={{ flexShrink: 0, marginLeft: 8, fontSize: 11, opacity: 0.55 }}>▾</span>
      </div>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 300,
          background: "#111827", border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: RADIUS.md, maxHeight: 220, overflowY: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.75)",
        }}>
          {options.map((o, i) => (
            <div
              key={o.value ?? i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onClick={() => { onChange(o.value); setOpen(false); setHovered(null); }}
              style={{
                padding: "10px 14px", cursor: "pointer",
                fontSize: inputStyle.fontSize, fontFamily: inputStyle.fontFamily,
                color: o.value === value ? "#fff" : "rgba(255,255,255,0.78)",
                background: hovered === i
                  ? "rgba(255,255,255,0.09)"
                  : o.value === value ? "rgba(91,142,255,0.18)" : "transparent",
              }}
            >
              {o.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LoginScreen({ onLogin, SA, GLASS, version, saLogoData, autoFaceId = true }) {
  const [airline,   setAirline]   = useState("UA");
  const [sccoList,  setSccoList]  = useState([]);
  const [sccoId,    setSccoId]    = useState("");

  // Biometric state — native UA only
  const native = Capacitor.isNativePlatform();
  const [bioAvailable,    setBioAvailable]    = useState(false);
  const [hasBioCreds,     setHasBioCreds]     = useState(false);
  const [bioLoading,      setBioLoading]      = useState(false);
  const [bioError,        setBioError]        = useState("");
  const [enableBioPrompt, setEnableBioPrompt] = useState(false);
  const [pendingLogin,    setPendingLogin]     = useState(null); // { session, refreshToken, sccoId }
  const [sccoLoading, setSccoLoading] = useState(true);
  const [password,  setPassword]  = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [error,     setError]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [locked,    setLocked]    = useState(false);
  const [failCount, setFailCount] = useState(0);
  // Load SCCO list whenever UA is selected
  useEffect(() => {
    if (airline === "UA") {
      setSccoLoading(true);
      setSccoId("");
      dbLoadSCCO().then(list => {
        const active = list.filter(s => s.active !== false);
        setSccoList(active);
        setSccoLoading(false);
      }).catch(() => {
        setSccoList([]);
        setSccoLoading(false);
      });
    } else {
      setSccoList([]);
      setSccoId("");
    }
  }, [airline]);

  // Check Face ID availability whenever airline changes (UA only, native only)
  useEffect(() => {
    if (!native || airline !== "UA") { setBioAvailable(false); setHasBioCreds(false); return; }
    Promise.all([biometricAvailable(), loadBioCreds()]).then(([avail, creds]) => {
      setBioAvailable(avail);
      setHasBioCreds(!!(creds?.airline));
    });
  }, [airline, native]);

  const handleBioLogin = async () => {
    setBioLoading(true);
    setBioError("");
    const result = await bioLogin();
    setBioLoading(false);
    if (!result || result.error === "cancelled") return;
    if (result.error === "session_expired") {
      setBioError("Session expired — sign in once with your password below to restore Face ID.");
      return;
    }
    onLogin(result.session);
  };

  const hasAutoTriggered = useRef(false);
  useEffect(() => {
    if (!autoFaceId || !bioAvailable || !hasBioCreds || hasAutoTriggered.current) return;
    hasAutoTriggered.current = true;
    handleBioLogin();
  }, [autoFaceId, bioAvailable, hasBioCreds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEnableBio = async () => {
    if (!pendingLogin) return;
    await saveBioCreds({
      airline:     pendingLogin.session.airline,
      sccoId:      pendingLogin.sccoId || null,
      partnerName: pendingLogin.session.partnerName || null,
      scco:        pendingLogin.session.scco || null,
    });
    onLogin(pendingLogin.session);
  };

  const handleSkipBio = () => onLogin(pendingLogin.session);

  const handleSubmit = async () => {
    if (airline === "UA" && !sccoId) { setError("Select your name to continue."); return; }
    if (!password.trim()) { setError("Enter your password."); return; }
    setLoading(true);
    setError("");
    try {
      const email = airline === "UA"
        ? `scco_${sccoId}@lax.internal`
        : `${airline.toLowerCase()}@lax.internal`;

      // 1. Check lockout before attempting auth
      const lockStatus = await dbCheckLockout(email);
      if (lockStatus.locked) {
        setLocked(true);
        setLoading(false);
        return;
      }

      // 2. Attempt sign-in
      const { data, error: authError } = await supabaseClient.auth.signInWithPassword({
        email, password,
      });

      if (authError) {
        // Record failure and check if we just triggered a lockout
        const result = await dbRecordFailedLogin(email, airline);
        if (result.locked) {
          setLocked(true);
        } else {
          const n = result.attempts || failCount + 1;
          setFailCount(n);
          setError(`Incorrect password — ${n} of 5 attempts used.`);
        }
        setLoading(false);
        return;
      }

      // 3. Success — clear attempt record using the just-issued token
      await dbResetLoginAttempts(data.session.access_token);

      // 4. Record the successful login (fire-and-forget; don't block the UI)
      const displayName = airline === "UA"
        ? (sccoList.find(s => String(s.id) === String(sccoId))?.name ?? null)
        : ((partnerName || "").trim() || null);
      dbRecordLogin(data.session.access_token, airline, displayName);

      const meta = data.user?.user_metadata ?? {};
      const scco = airline === "UA" ? (sccoList.find(s => String(s.id) === String(sccoId)) || null) : null;
      const session = meta.role === "ua_scco"
        ? { airline: "UA", isUA: true, scco, jwt: true, meta }
        : { airline: meta.airline, isUA: false, scco: null, partnerName: (partnerName || "").trim() || null, jwt: true, meta };

      saveSession(session);

      // Offer Face ID setup on native for UA SCCOs who don't have it yet
      if (native && bioAvailable && !hasBioCreds && session.isUA) {
        setPendingLogin({ session, sccoId });
        setEnableBioPrompt(true);
        setLoading(false);
        return;
      }

      onLogin(session);
    } catch (e) {
      setError("Connection error. Try again.");
    }
    setLoading(false);
  };

  const inputStyle = {
    display: "block", width: "100%", boxSizing: "border-box", height: 48,
    backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
    background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.18)",
    color: SA.white, padding: "0 14px", fontSize: TYPOGRAPHY.sub, borderRadius: RADIUS.md,
    fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", outline: "none", appearance: "none",
    WebkitAppearance: "none", MozAppearance: "none", margin: 0,
    transition: "border-color 0.15s",
  };

  const isUA = airline === "UA";
  const canSubmit = !loading && !locked && (isUA ? !!sccoId : true);

  const cardStyle = {
    backdropFilter: "blur(40px) saturate(200%)",
    WebkitBackdropFilter: "blur(40px) saturate(200%)",
    background: "rgba(22,28,50,0.52)",
    border: "1px solid rgba(255,255,255,0.16)",
    borderTop: "1.5px solid rgba(255,255,255,0.32)",
    boxShadow: "inset 0 1.5px 0 rgba(255,255,255,0.24), 0 24px 64px rgba(0,0,0,0.50), 0 4px 16px rgba(0,0,0,0.25)",
    borderRadius: 28, padding: "32px 28px",
    boxSizing: "border-box", width: "100%",
  };

  return (
    <div style={{ minHeight: "100vh",
      background: "linear-gradient(135deg,#060912 0%,#080E1C 45%,#0A1020 70%,#060912 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: "24px", boxSizing: "border-box",
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
      position: "relative", overflow: "hidden" }}>

      <style>{`@keyframes loginLogoSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Ambient blobs */}
      <div style={{ pointerEvents: "none", position: "absolute", inset: 0, zIndex: 0 }}>
        <div style={{ position: "absolute", width: 500, height: 500, borderRadius: RADIUS.circle,
          background: "radial-gradient(circle,rgba(91,142,255,0.10) 0%,transparent 70%)",
          top: "-15%", left: "-10%", animation: "ambientFloat 18s ease-in-out infinite" }} />
        <div style={{ position: "absolute", width: 400, height: 400, borderRadius: RADIUS.circle,
          background: "radial-gradient(circle,rgba(120,80,255,0.07) 0%,transparent 70%)",
          bottom: "0%", right: "-5%", animation: "ambientFloat 24s ease-in-out infinite reverse" }} />
      </div>

      <div style={{ width: "100%", maxWidth: 420, boxSizing: "border-box",
        marginLeft: "auto", marginRight: "auto", position: "relative", zIndex: 1 }}>

        {/* Header */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, marginBottom: 52 }}>
          <img src={saLogoData} alt="Star Alliance" style={{ width: 80, height: 80, objectFit: "contain", opacity: 0.95,
            animation: loading ? "loginLogoSpin 0.7s linear infinite" : "none" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 26, color: SA.lightSilver, letterSpacing: "0.44em", fontWeight: 300 }}>STAR ALLIANCE</div>
            <div style={{ fontSize: TYPOGRAPHY.caption, color: SA.silver, letterSpacing: "0.26em", marginTop: 8, fontWeight: 400 }}>LAX CONNECT</div>
          </div>
        </div>

        <div style={cardStyle}>

          {/* ── Enable Face ID prompt (replaces form after first password login) ── */}
          {enableBioPrompt && (
            <div style={{ textAlign: "center", padding: "8px 0" }}>
              <div style={{ fontSize: 48, marginBottom: 20, lineHeight: 1 }}>🔒</div>
              <div style={{ fontSize: TYPOGRAPHY.header, fontWeight: 600, color: SA.white,
                letterSpacing: "-0.02em", marginBottom: 12 }}>
                Enable Face ID?
              </div>
              <div style={{ fontSize: TYPOGRAPHY.note, color: SA.silver, lineHeight: 1.65, marginBottom: 32 }}>
                Sign in instantly next time without typing your password.
              </div>
              <button onClick={handleEnableBio}
                style={{ width: "100%", borderRadius: 12, padding: "14px",
                  fontSize: TYPOGRAPHY.sub, fontWeight: 600,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                  backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                  background: "rgba(91,142,255,0.88)",
                  border: "1px solid rgba(255,255,255,0.22)",
                  boxShadow: "inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 16px rgba(91,142,255,0.4)",
                  color: "#fff", cursor: "pointer", marginBottom: 10 }}>
                Enable Face ID
              </button>
              <button onClick={handleSkipBio}
                style={{ width: "100%", borderRadius: 12, padding: "14px",
                  fontSize: TYPOGRAPHY.sub, fontWeight: 400,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  color: "rgba(255,255,255,0.55)", cursor: "pointer" }}>
                Skip
              </button>
            </div>
          )}

          {/* ── Normal login form ── */}
          {!enableBioPrompt && <>

          {/* Airline row */}
          <div style={{ marginBottom: 18 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              fontWeight: 300, marginBottom: 7, letterSpacing: "0.08em" }}>Airline</label>
            <CustomSelect
              value={airline}
              onChange={v => { setAirline(v); setError(""); setPassword(""); setPartnerName(""); setLocked(false); setFailCount(0); }}
              options={[
                { value: "UA",  label: "UA — United Airlines" },
                { value: "LHG", label: "LHG — Lufthansa Group" },
                ...SA_AIRLINES
                  .filter(a => a.code !== "UA" && a.code !== "LHG" && !LHG_CARRIERS.includes(a.code))
                  .map(a => ({ value: a.code, label: `${a.code} — ${a.name}` })),
              ]}
              inputStyle={inputStyle}
            />
          </div>

          {/* SCCO selector — UA only */}
          {isUA && (
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
                fontWeight: 500, marginBottom: 7, letterSpacing: "0.02em" }}>SCCO</label>
              {sccoLoading ? (
                <div style={{ ...inputStyle, fontSize: TYPOGRAPHY.sub, color: SA.borderGray }}>Loading agents…</div>
              ) : sccoList.length === 0 ? (
                <div style={{ background: SA.darkGray, border: `1px solid ${SA.borderGray}`,
                  borderRadius: RADIUS.md, padding: "14px 16px", fontSize: TYPOGRAPHY.body, color: SA.silver, lineHeight: 1.6 }}>
                  No agents in roster yet — run setup SQL in Supabase.
                </div>
              ) : (
                <CustomSelect
                  value={sccoId}
                  onChange={v => { setSccoId(v); setError(""); setPassword(""); }}
                  options={[
                    { value: "", label: "Select your name…" },
                    ...sccoList.map(s => ({ value: String(s.id), label: s.name + (s.badge ? "  ·  #" + s.badge : "") })),
                  ]}
                  inputStyle={inputStyle}
                  placeholder="Select your name…"
                />
              )}
            </div>
          )}

          {/* Lockout banner */}
          {locked && (
            <div style={{ marginBottom: 20, background: "rgba(180,50,50,0.18)", border: "1px solid rgba(200,60,60,0.4)", borderRadius: RADIUS.md, padding: "16px 18px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                  <rect x="3" y="7" width="10" height="7.5" rx="1.5" stroke="#D46060" strokeWidth="1.3"/>
                  <path d="M5 7V5C5 3.343 6.343 2 8 2C9.657 2 11 3.343 11 5V7" stroke="#D46060" strokeWidth="1.3" strokeLinecap="round"/>
                  <circle cx="8" cy="10.5" r="1" fill="#D46060"/>
                </svg>
                <span style={{ fontSize: TYPOGRAPHY.sub, fontWeight: 700, color: "#D46060", letterSpacing: "-0.01em" }}>Account Locked</span>
              </div>
              <div style={{ fontSize: TYPOGRAPHY.body, color: "rgba(255,255,255,0.72)", lineHeight: 1.55 }}>
                This account has been locked after 5 failed login attempts. Please contact a United Airlines Admin SCCO to reset your access.
              </div>
            </div>
          )}

          {/* Password */}
          <div style={{ marginBottom: !isUA ? 16 : 24 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
              fontWeight: 300, marginBottom: 7, letterSpacing: "0.08em" }}>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && canSubmit && handleSubmit()}
              placeholder="Enter password"
              style={{ ...inputStyle, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", letterSpacing: "0.1em" }}
            />
          </div>

          {/* Optional name — partner only */}
          {!isUA && (
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver,
                fontWeight: 500, marginBottom: 7, letterSpacing: "0.02em" }}>
                Your Name <span style={{ color: SA.borderGray, fontWeight: 400 }}>(optional)</span>
              </label>
              <input
                type="text"
                value={partnerName}
                onChange={e => setPartnerName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && canSubmit && handleSubmit()}
                placeholder="e.g. Maria S."
                style={{ ...inputStyle }}
              />
              <div style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, marginTop: 6, lineHeight: 1.5 }}>
                If entered, your name will appear in messages and remarks.
              </div>
            </div>
          )}

          {error && (
            <div style={{ fontSize: TYPOGRAPHY.body, color: "#D46060", marginBottom: 16, fontWeight: 500, lineHeight: 1.4 }}>{error}</div>
          )}

          <button onClick={handleSubmit} disabled={!canSubmit}
            style={{ width: "100%", borderRadius: 12, padding: "14px", fontSize: TYPOGRAPHY.sub,
              fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              fontWeight: 400, letterSpacing: "0.08em",
              ...(canSubmit ? {
                backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
                background: "rgba(91,142,255,0.88)",
                border: "1px solid rgba(255,255,255,0.22)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.28), 0 4px 16px rgba(91,142,255,0.4)",
              } : {
                background: "rgba(255,255,255,0.08)",
                border: "1px solid rgba(255,255,255,0.06)",
              }),
              color: canSubmit ? "#fff" : "rgba(255,255,255,0.3)",
              cursor: canSubmit ? "pointer" : "not-allowed",
              transition: "all 0.2s", opacity: loading ? 0.7 : 1 }}>
            {loading ? "Signing in…" : "Sign In →"}
          </button>

          {/* Face ID sign-in — native UA with saved credentials */}
          {bioAvailable && hasBioCreds && airline === "UA" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "18px 0 14px" }}>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.10)" }} />
                <span style={{ fontSize: TYPOGRAPHY.label, color: SA.borderGray, letterSpacing: "0.12em" }}>OR</span>
                <div style={{ flex: 1, height: 1, background: "rgba(255,255,255,0.10)" }} />
              </div>
              {bioError && (
                <div style={{ fontSize: TYPOGRAPHY.body, color: "#D46060", marginBottom: 12,
                  fontWeight: 500, lineHeight: 1.4 }}>{bioError}</div>
              )}
              <button onClick={handleBioLogin} disabled={bioLoading}
                style={{ width: "100%", borderRadius: 12, padding: "14px", fontSize: TYPOGRAPHY.sub,
                  fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
                  fontWeight: 400, letterSpacing: "0.04em",
                  background: "rgba(255,255,255,0.07)",
                  border: "1px solid rgba(255,255,255,0.16)",
                  color: bioLoading ? "rgba(255,255,255,0.35)" : "rgba(255,255,255,0.88)",
                  cursor: bioLoading ? "not-allowed" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7 3H5C3.895 3 3 3.895 3 5V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M17 3H19C20.105 3 21 3.895 21 5V7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M7 21H5C3.895 21 3 20.105 3 19V17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M17 21H19C20.105 21 21 20.105 21 19V17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6"/>
                  <path d="M12 7C9.239 7 7 9.239 7 12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                  <path d="M17 12C17 14.761 14.761 17 12 17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                </svg>
                {bioLoading ? "Authenticating…" : "Sign in with Face ID"}
              </button>
            </>
          )}

          </> /* end normal form */}
        </div>

        <div style={{ textAlign: "center", marginTop: 24, fontSize: TYPOGRAPHY.caption, color: SA.borderGray,
          letterSpacing: "0.1em", fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
          v{version}
        </div>
      </div>
    </div>
  );
}

export { LoginScreen };
